import React, { useRef, useState, useEffect } from "react";
import {
  Plus,
  Search,
  Quote,
  Image,
  Link,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Upload,
  ExternalLink,
  Trash2,
  Edit3,
  Type,
  List,
  ListOrdered,
  ChevronLeft,
  ChevronRight,
  FileText,
  MoreHorizontal,
  ChevronDown,
  Hash,
  TreePine,
  GripVertical,
  Logs,
  Slash,
  Table,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { FaParagraph } from "react-icons/fa6";
import { FaHeading } from "react-icons/fa6";
import { GoListUnordered } from "react-icons/go";
import { BsFillChatLeftQuoteFill } from "react-icons/bs";
import { IoImagesOutline } from "react-icons/io5";
import { FaRegImages } from "react-icons/fa6";
import { ImBold } from "react-icons/im";
import { MdKeyboardArrowUp } from "react-icons/md";
import { MdKeyboardArrowDown } from "react-icons/md";
import { PiDotsSixVertical } from "react-icons/pi";
import { RxSlash } from "react-icons/rx";
// import { HiMiniSlash } from "react-icons/hi2";
import { FaTable } from "react-icons/fa";
import { add_blog } from "../../utils/apis";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Textarea,
  useDisclosure,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AbsoluteCenter,
  Center,
  useToast,
  Tooltip,
  Icon,
  Spacer,
  HStack,
  VStack,
  Stack,
} from "@chakra-ui/react";

const ToolbarButton = ({
  icon: Icon,
  onClick,
  isActive = false,
  label,
  variant = "default",
}) => (
  <IconButton
    onMouseDown={(e) => {
      e.preventDefault(); // Prevent focus loss
      e.stopPropagation();
    }}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    color={isActive ? "black" : "black"}
    bg={isActive ? "gray.200" : "transparent"}
    _hover={{
      bg: variant === "danger" ? "red.50" : "gray.100",
      color: variant === "danger" ? "red.600" : "black",
    }}
    aria-label={label}
    icon={<Icon size={20} />}
    size="sm"
    variant="ghost"
    borderRadius="md"
  />
);

export default function BlogAdminInterface() {
  const location = useLocation();
  const isEditMode = location.state?.type === "Edit";
  const blogData = location.state?.blogData || null;

  const fileInputRef = useRef();
  const editorRef = useRef();
  const editorWrapperRef = useRef();
  const [activeToolbar, setActiveToolbar] = useState("default");
  const [editorContent, setEditorContent] = useState("");
  const [lineHeight, setLineHeight] = useState(1.6);
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const [showTableGrid, setShowTableGrid] = useState(false);
  const [tableSize, setTableSize] = useState({ rows: 0, cols: 0 });
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [resizing, setResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartSize, setResizeStartSize] = useState({
    width: 0,
    height: 0,
  });
  const [tableToolbarPos, setTableToolbarPos] = useState({
    visible: false,
    top: 0,
    left: 0,
  });
  const [handlesRect, setHandlesRect] = useState(null);
  const [openTableMenu, setOpenTableMenu] = useState(null);
  const {
    isOpen: isMenuOpen,
    onToggle: onToggleMenu,
    onClose: onCloseMenu,
  } = useDisclosure();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Add to your existing state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  const [formData, setFormData] = useState({
    title: blogData?.title || "",
    altTitle: blogData?.alt_text || "",
    metaTitle: blogData?.meta_title || "",
    metaDescription: blogData?.meta_description || "",
    focusKey: blogData?.focus_key || "",
    image: blogData?.blogImage,
    id: blogData?.id || null, // Add id field for updates
  });
  useEffect(() => {
    if (isEditMode && blogData && editorRef.current) {
      editorRef.current.innerHTML = blogData.description;
      setEditorContent(blogData.description);
    }
  }, [isEditMode, blogData]);

  const [searchTerm, setSearchTerm] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [blogImage, setBlogImage] = useState(null);
  const [submittedBlog, setSubmittedBlog] = useState(null);

  const repositionTableUI = () => {
    if (!selectedTable || !editorWrapperRef.current) {
      setTableToolbarPos({ visible: false, top: 0, left: 0 });
      setHandlesRect(null);
      return;
    }

    const tableRect = selectedTable.getBoundingClientRect();
    const wrapperRect = editorWrapperRef.current.getBoundingClientRect();

    setTableToolbarPos({
      visible: true,
      top: tableRect.top - wrapperRect.top - 40,
      left: tableRect.left - wrapperRect.left + tableRect.width / 2 - 100,
    });

    setHandlesRect({
      top: tableRect.top - wrapperRect.top,
      left: tableRect.left - wrapperRect.left,
      width: tableRect.width,
      height: tableRect.height,
    });
  };

  useEffect(() => {
    repositionTableUI();
  }, [selectedTable, selectedCell]);

  useEffect(() => {
    const handleResize = () => repositionTableUI();
    const handleScroll = () => repositionTableUI();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editorRef.current && !editorRef.current.contains(event.target)) {
        setSelectedTable(null);
        setSelectedCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleTableClick = (e) => {
      const cell = e.target.closest("td");
      const table = e.target.closest("table");

      if (cell && table) {
        setSelectedCell(cell);
        setSelectedTable(table);
        setActiveToolbar("table");
      } else if (table) {
        setSelectedTable(table);
        setActiveToolbar("table");
        setSelectedCell(null);
      } else {
        setSelectedTable(null);
        setSelectedCell(null);
        if (activeToolbar === "table") {
          setActiveToolbar("default");
        }
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener("click", handleTableClick);
    }

    return () => {
      if (editor) {
        editor.removeEventListener("click", handleTableClick);
      }
    };
  }, [activeToolbar]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizing || !selectedCell) return;

      const deltaX = e.clientX - resizeStartPos.x;
      const deltaY = e.clientY - resizeStartPos.y;

      if (resizeDirection === "right") {
        selectedCell.style.width = `${resizeStartSize.width + deltaX}px`;
      } else if (resizeDirection === "bottom") {
        selectedCell.style.height = `${resizeStartSize.height + deltaY}px`;
      } else if (resizeDirection === "corner") {
        selectedCell.style.width = `${resizeStartSize.width + deltaX}px`;
        selectedCell.style.height = `${resizeStartSize.height + deltaY}px`;
      }
    };

    const handleMouseUp = () => {
      setResizing(false);
      setResizeDirection(null);
      handleEditorChange();
    };

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    resizing,
    resizeDirection,
    selectedCell,
    resizeStartPos,
    resizeStartSize,
  ]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const handleDragOver = (e) => {
        e.preventDefault();
      };

      const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          const file = files[0];
          if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = () => {
              const img = document.createElement("img");
              img.src = reader.result;
              img.style.maxWidth = "100%";
              img.style.height = "auto";
              img.style.display = "block";
              img.style.margin = "10px 0";
              insertElementAtCursor(img);
              setActiveToolbar("image");
            };
            reader.readAsDataURL(file);
          }
        }
      };

      editor.addEventListener("dragover", handleDragOver);
      editor.addEventListener("drop", handleDrop);

      return () => {
        editor.removeEventListener("dragover", handleDragOver);
        editor.removeEventListener("drop", handleDrop);
      };
    }
  }, []);

  const insertRow = (position) => {
    if (!selectedTable) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const cols = tbody.rows[0]?.cells.length || 0;

    if (cols === 0) return;

    const newRow = document.createElement("tr");

    for (let i = 0; i < cols; i++) {
      const td = createTableCell();
      newRow.appendChild(td);
    }

    if (selectedCell) {
      const selectedRow = selectedCell.parentNode;
      if (position === "above") {
        selectedRow.parentNode.insertBefore(newRow, selectedRow);
      } else {
        selectedRow.parentNode.insertBefore(newRow, selectedRow.nextSibling);
      }
    } else {
      tbody.appendChild(newRow);
    }

    if (newRow.cells.length > 0) {
      setSelectedCell(newRow.cells[0]);
    }

    handleEditorChange();
    repositionTableUI();
  };

  const insertColumn = (position) => {
    if (!selectedTable) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;

    if (rows.length === 0) return;

    const colIndex = selectedCell
      ? selectedCell.cellIndex
      : rows[0].cells.length;
    const insertIndex = position === "left" ? colIndex : colIndex + 1;

    for (let i = 0; i < rows.length; i++) {
      const td = createTableCell();

      if (insertIndex >= 0 && insertIndex < rows[i].cells.length) {
        rows[i].insertBefore(td, rows[i].cells[insertIndex]);
      } else {
        rows[i].appendChild(td);
      }
    }

    if (rows.length > 0) {
      const newCell =
        rows[0].cells[
          insertIndex >= rows[0].cells.length
            ? rows[0].cells.length - 1
            : insertIndex
        ];
      if (newCell) {
        setSelectedCell(newCell);
      } else {
        setSelectedCell(null);
      }
    } else {
      setSelectedCell(null);
    }

    handleEditorChange();
    repositionTableUI();
  };

  const removeRow = () => {
    if (!selectedTable || !selectedCell) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;

    if (rows.length <= 1) {
      toast({
        title: "Cannot delete the last row",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const row = selectedCell.parentNode;
    const rowIndex = Array.from(tbody.rows).indexOf(row);

    row.remove();

    if (tbody.rows.length > 0) {
      const newRowIndex = Math.min(rowIndex, tbody.rows.length - 1);
      const newCell =
        tbody.rows[newRowIndex]?.cells[selectedCell.cellIndex] ||
        tbody.rows[newRowIndex]?.cells[0];
      if (newCell) {
        setSelectedCell(newCell);
      } else {
        setSelectedCell(null);
      }
    } else {
      setSelectedCell(null);
    }

    handleEditorChange();
    repositionTableUI();
  };

  const removeColumn = () => {
    if (!selectedTable || !selectedCell) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;
    const colIndex = selectedCell.cellIndex;

    if (rows[0]?.cells.length <= 1) {
      toast({
        title: "Cannot delete the last column",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].cells[colIndex]) {
        rows[i].deleteCell(colIndex);
      }
    }

    if (rows.length > 0 && rows[0].cells.length > 0) {
      const newColIndex = Math.min(colIndex, rows[0].cells.length - 1);
      const newCell = rows[0]?.cells[newColIndex];
      if (newCell) {
        setSelectedCell(newCell);
      } else {
        setSelectedCell(null);
      }
    } else {
      setSelectedCell(null);
    }

    handleEditorChange();
    repositionTableUI();
  };

  const removeTable = () => {
    if (!selectedTable) return;

    selectedTable.remove();
    setSelectedTable(null);
    setSelectedCell(null);
    setActiveToolbar("default");
    handleEditorChange();
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      setEditorContent(editorRef.current.innerHTML);
    }
  };

  const saveSelection = () => {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        return sel.getRangeAt(0);
      }
    }
    return null;
  };

  const restoreSelection = (range) => {
    if (range) {
      if (window.getSelection) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const execCommand = (command, value = null) => {
    const range = saveSelection();

    if (editorRef.current) {
      editorRef.current.focus();
    }

    if (range) {
      restoreSelection(range);
    }

    document.execCommand(command, false, value);

    setTimeout(() => {
      handleEditorChange();
    }, 10);
  };

  const insertElementAtCursor = (element) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.appendChild(element);

    const spacer = document.createElement("p");
    spacer.innerHTML = "<br>";
    editor.appendChild(spacer);

    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(spacer);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    handleEditorChange();
  };

  const addImagesFromFile = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = document.createElement("img");
        img.src = reader.result;
        img.style.maxWidth = "100%";
        img.style.height = "auto";
        img.style.display = "block";
        img.style.margin = "20px 0";
        img.alt = "Uploaded image";

        insertElementAtCursor(img);
      };
      reader.readAsDataURL(file);
    });

    event.target.value = "";
  };

  const addImageFromURL = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = "Inserted from URL";
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.display = "block";
      img.style.margin = "10px 0";

      insertElementAtCursor(img);
      setActiveToolbar("image");
    }
  };

  const addLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    if (!selectedText) {
      toast({
        title: "Please select some text first to create a link",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const url = prompt("Enter URL:", "https://");
    if (url) {
      // Create a range from the current selection
      const range = selection.getRangeAt(0);

      // Create a new anchor element
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.style.color = "#3182ce"; // blue.500
      anchor.style.textDecoration = "underline";
      anchor.style.cursor = "pointer";

      // Delete the selected content and insert the anchor
      range.deleteContents();
      range.insertNode(anchor);

      // Put the selected text inside the anchor
      anchor.textContent = selectedText;

      // Restore selection after the link
      const newRange = document.createRange();
      newRange.setStartAfter(anchor);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      setActiveToolbar("link");
      handleEditorChange();
    }
  };

  const handleBlogImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFormChange("blogImage", file);
    }
  };

  const removeSelectedElement = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      if (range.collapsed) {
        let element = range.startContainer;
        if (element.nodeType === Node.TEXT_NODE) {
          element = element.parentElement;
        }

        if (element.tagName === "IMG") {
          element.remove();
        } else if (element.tagName === "A") {
          const textNode = document.createTextNode(element.textContent);
          element.parentNode.replaceChild(textNode, element);
        } else if (element.tagName === "TABLE") {
          element.remove();
          setSelectedTable(null);
          setSelectedCell(null);
          setActiveToolbar("default");
        }
      } else {
        range.deleteContents();
      }

      handleEditorChange();
    }
  };

  const formatBlock = (tagName, selectedText = null) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const editor = editorRef.current;

    if (!range.collapsed && selectedText) {
      const heading = document.createElement(tagName);
      heading.textContent = selectedText;

      heading.style.fontSize =
        tagName === "h1"
          ? "32px"
          : tagName === "h2"
          ? "28px"
          : tagName === "h3"
          ? "24px"
          : tagName === "h4"
          ? "20px"
          : tagName === "h5"
          ? "18px"
          : "16px";
      heading.style.fontWeight = "bold";
      heading.style.margin = "10px 0";

      range.deleteContents();
      range.insertNode(heading);

      const newRange = document.createRange();
      newRange.setStartAfter(heading);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      const isEditorEmpty = !editor.innerText.trim();
      const heading = document.createElement(tagName);

      if (isEditorEmpty) {
        heading.textContent = "Heading ";
        heading.style.fontSize =
          tagName === "h1"
            ? "32px"
            : tagName === "h2"
            ? "28px"
            : tagName === "h3"
            ? "24px"
            : tagName === "h4"
            ? "20px"
            : tagName === "h5"
            ? "18px"
            : "16px";
        heading.style.fontWeight = "bold";
        heading.style.margin = "10px 0";
        editor.appendChild(heading);
      } else {
        let element = range.commonAncestorContainer;
        if (element.nodeType === Node.TEXT_NODE) {
          element = element.parentElement;
        }

        if (element !== editor) {
          heading.innerHTML =
            element.innerHTML || element.textContent || "Heading ";
          element.parentNode.replaceChild(heading, element);
        } else {
          heading.innerHTML = "Heading ";
          editor.appendChild(heading);
        }
      }

      range.selectNodeContents(heading);
      range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
    handleEditorChange();
  };

  const insertList = (listType) => {
    const selection = window.getSelection();
    const editor = editorRef.current;

    if (!editor) return;

    if (selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed) {
      const range = selection.getRangeAt(0);
      let selectedElement = range.commonAncestorContainer;

      if (selectedElement.nodeType === Node.TEXT_NODE) {
        selectedElement = selectedElement.parentElement;
      }

      if (
        selectedElement.tagName === "H1" ||
        selectedElement.tagName === "H2" ||
        selectedElement.tagName === "H3" ||
        selectedElement.tagName === "P"
      ) {
        const content = selectedElement.innerHTML;

        let list, listStyleType;
        switch (listType) {
          case "bullet":
            list = document.createElement("ul");
            listStyleType = "disc";
            break;
          case "numbered":
            list = document.createElement("ol");
            listStyleType = "decimal";
            break;
          case "alphabetical":
            list = document.createElement("ol");
            listStyleType = "lower-alpha";
            break;
          default:
            list = document.createElement("ul");
            listStyleType = "disc";
        }

        list.style.listStyleType = listStyleType;
        list.style.paddingLeft = "20px";
        list.style.marginLeft = "20px";

        const listItem = document.createElement("li");
        listItem.innerHTML = content;
        listItem.style.display = "list-item";
        list.appendChild(listItem);

        selectedElement.parentNode.replaceChild(list, selectedElement);

        const newRange = document.createRange();
        newRange.selectNodeContents(listItem);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleEditorChange();
        return;
      }
    }

    editor.focus();

    let selection2 = window.getSelection();
    if (
      !selection2 ||
      selection2.rangeCount === 0 ||
      !editor.contains(selection2.anchorNode)
    ) {
      const range = document.createRange();

      if (editor.innerHTML.trim() === "") {
        const emptyNode = document.createTextNode("");
        editor.appendChild(emptyNode);
      }

      range.selectNodeContents(editor);
      range.collapse(false);
      selection2 = window.getSelection();
      selection2.removeAllRanges();
      selection2.addRange(range);
    }

    let list, listStyleType;
    switch (listType) {
      case "bullet":
        list = document.createElement("ul");
        listStyleType = "disc";
        break;
      case "numbered":
        list = document.createElement("ol");
        listStyleType = "decimal";
        break;
      case "alphabetical":
        list = document.createElement("ol");
        listStyleType = "lower-alpha";
        break;
      default:
        list = document.createElement("ul");
        listStyleType = "disc";
    }

    list.style.listStyleType = listStyleType;
    list.style.paddingLeft = "20px";
    list.style.marginLeft = "20px";

    const listItem = document.createElement("li");
    listItem.innerHTML = "";
    listItem.style.display = "list-item";
    list.appendChild(listItem);

    insertElementAtCursor(list);

    const newRange = document.createRange();
    newRange.selectNodeContents(listItem);
    newRange.collapse(false);
    selection2.removeAllRanges();
    selection2.addRange(newRange);

    handleEditorChange();
  };

  const insertParagraph = () => {
    const p = document.createElement("p");
    p.innerHTML = "Paragraph text";
    insertElementAtCursor(p);

    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(p);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const insertQuote = () => {
    const selection = window.getSelection();
    const editor = editorRef.current;

    if (!editor) return;

    if (selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed) {
      const range = selection.getRangeAt(0);
      let selectedElement = range.commonAncestorContainer;

      if (selectedElement.nodeType === Node.TEXT_NODE) {
        selectedElement = selectedElement.parentElement;
      }

      if (
        selectedElement.tagName === "H1" ||
        selectedElement.tagName === "H2" ||
        selectedElement.tagName === "H3" ||
        selectedElement.tagName === "P"
      ) {
        const content = selectedElement.innerHTML;

        const blockquote = document.createElement("blockquote");
        blockquote.innerHTML = content;
        blockquote.style.fontSize = "15px";
        blockquote.style.marginLeft = "0";
        blockquote.style.paddingLeft = "16px";
        blockquote.style.color = "black";
        blockquote.style.borderLeft = "4px solid #ccc";

        selectedElement.parentNode.replaceChild(blockquote, selectedElement);

        const newRange = document.createRange();
        newRange.selectNodeContents(blockquote);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleEditorChange();
        return;
      }
    }

    if (
      !selection ||
      selection.rangeCount === 0 ||
      !editor.contains(selection.anchorNode)
    ) {
      editor.focus();
      const range = document.createRange();

      if (editor.innerHTML.trim() === "" || editor.innerHTML === "<br>") {
        const placeholder = document.createElement("p");
        placeholder.innerHTML = "<br>";
        editor.appendChild(placeholder);
      }

      const lastChild = editor.lastChild;
      if (lastChild) {
        range.setStart(lastChild, lastChild.childNodes?.length || 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    const blockquote = document.createElement("blockquote");
    blockquote.innerHTML = '""';
    blockquote.style.fontSize = "20px";
    blockquote.style.marginLeft = "0";
    blockquote.style.paddingLeft = "16px";
    blockquote.style.fontStyle = "italic";
    blockquote.style.color = "black";
    blockquote.style.borderLeft = "4px solid #ccc";

    insertElementAtCursor(blockquote);

    const range = document.createRange();
    range.selectNodeContents(blockquote);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const createTableCell = () => {
    const td = document.createElement("td");
    td.style.border = "1px solid #ddd";
    td.style.padding = "8px";
    td.style.minWidth = "50px";
    td.style.height = "30px";
    td.style.position = "relative";
    td.contentEditable = "true";

    const resizeHandleRight = document.createElement("div");
    resizeHandleRight.style.position = "absolute";
    resizeHandleRight.style.right = "-4px";
    resizeHandleRight.style.top = "0";
    resizeHandleRight.style.bottom = "0";
    resizeHandleRight.style.width = "8px";
    resizeHandleRight.style.cursor = "col-resize";
    resizeHandleRight.style.zIndex = "10";
    resizeHandleRight.contentEditable = "false";
    resizeHandleRight.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);
      setResizeDirection("right");
      setSelectedCell(td);
      setResizeStartPos({ x: e.clientX, y: e.clientY });
      setResizeStartSize({
        width: parseInt(td.style.width || "0") || td.offsetWidth,
        height: parseInt(td.style.height || "0") || td.offsetHeight,
      });
    });
    td.appendChild(resizeHandleRight);

    const resizeHandleBottom = document.createElement("div");
    resizeHandleBottom.style.position = "absolute";
    resizeHandleBottom.style.left = "0";
    resizeHandleBottom.style.right = "0";
    resizeHandleBottom.style.bottom = "-4px";
    resizeHandleBottom.style.height = "8px";
    resizeHandleBottom.style.cursor = "row-resize";
    resizeHandleBottom.style.zIndex = "10";
    resizeHandleBottom.contentEditable = "false";
    resizeHandleBottom.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);
      setResizeDirection("bottom");
      setSelectedCell(td);
      setResizeStartPos({ x: e.clientX, y: e.clientY });
      setResizeStartSize({
        width: parseInt(td.style.width || "0") || td.offsetWidth,
        height: parseInt(td.style.height || "0") || td.offsetHeight,
      });
    });
    td.appendChild(resizeHandleBottom);

    const resizeHandleCorner = document.createElement("div");
    resizeHandleCorner.style.position = "absolute";
    resizeHandleCorner.style.right = "-4px";
    resizeHandleCorner.style.bottom = "-4px";
    resizeHandleCorner.style.width = "8px";
    resizeHandleCorner.style.height = "8px";
    resizeHandleCorner.style.cursor = "nwse-resize";
    resizeHandleCorner.style.zIndex = "10";
    resizeHandleCorner.contentEditable = "false";
    resizeHandleCorner.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);
      setResizeDirection("corner");
      setSelectedCell(td);
      setResizeStartPos({ x: e.clientX, y: e.clientY });
      setResizeStartSize({
        width: parseInt(td.style.width || "0") || td.offsetWidth,
        height: parseInt(td.style.height || "0") || td.offsetHeight,
      });
    });
    td.appendChild(resizeHandleCorner);

    return td;
  };

  const insertTable = (rows, cols) => {
    const table = document.createElement("table");
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
    table.style.margin = "10px 0";
    table.style.border = "1px solid #ddd";

    const tbody = document.createElement("tbody");

    for (let i = 0; i < rows; i++) {
      const tr = document.createElement("tr");

      for (let j = 0; j < cols; j++) {
        const td = createTableCell();
        tr.appendChild(td);
      }

      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    insertElementAtCursor(table);

    setActiveToolbar("table");
    setSelectedTable(table);
    setSelectedCell(table.querySelector("td"));
    setShowTableGrid(false);
  };

  const addTableRow = () => {
    if (!selectedTable) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const cols = tbody.rows[0]?.cells.length || 0;

    if (cols === 0) return;

    const newRow = document.createElement("tr");

    for (let i = 0; i < cols; i++) {
      const td = createTableCell();
      newRow.appendChild(td);
    }

    if (selectedCell) {
      const selectedRow = selectedCell.parentNode;
      selectedRow.parentNode.insertBefore(newRow, selectedRow.nextSibling);
    } else {
      tbody.appendChild(newRow);
    }

    setSelectedTable(selectedTable);
    if (selectedCell) {
      setSelectedCell(selectedCell);
    }

    handleEditorChange();
  };

  const addTableColumn = () => {
    if (!selectedTable) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;

    if (rows.length === 0) return;

    const colIndex = selectedCell
      ? selectedCell.cellIndex
      : rows[0].cells.length;

    for (let i = 0; i < rows.length; i++) {
      const td = createTableCell();

      if (colIndex >= 0 && colIndex < rows[i].cells.length) {
        rows[i].insertBefore(td, rows[i].cells[colIndex + 1]);
      } else {
        rows[i].appendChild(td);
      }
    }

    setSelectedTable(selectedTable);
    if (selectedCell) {
      setSelectedCell(selectedCell);
    }

    handleEditorChange();
  };

  const deleteTableRow = () => {
    if (!selectedTable || !selectedCell) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;

    if (rows.length <= 1) {
      toast({
        title: "Cannot delete the last row",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const row = selectedCell.parentNode;
    const rowIndex = Array.from(tbody.rows).indexOf(row);

    row.remove();

    if (tbody.rows.length > 0) {
      const newRowIndex = Math.min(rowIndex, tbody.rows.length - 1);
      const newCell = tbody.rows[newRowIndex]?.cells[0];
      if (newCell) {
        setSelectedCell(newCell);
      } else {
        setSelectedCell(null);
      }
    } else {
      setSelectedCell(null);
    }

    handleEditorChange();
  };

  const deleteTableColumn = () => {
    if (!selectedTable || !selectedCell) return;

    const tbody = selectedTable.querySelector("tbody") || selectedTable;
    const rows = tbody.rows;
    const colIndex = selectedCell.cellIndex;

    if (rows[0].cells.length <= 1) {
      toast({
        title: "Cannot delete the last column",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].cells[colIndex]) {
        rows[i].deleteCell(colIndex);
      }
    }

    const newColIndex = Math.min(colIndex, rows[0].cells.length - 1);
    const newCell = rows[0]?.cells[newColIndex];
    if (newCell) {
      setSelectedCell(newCell);
    } else {
      setSelectedCell(null);
    }

    handleEditorChange();
  };

  const debugTableSelection = () => {
    console.log("=== TABLE DEBUG INFO ===");
    console.log("Selected Table:", selectedTable);
    console.log("Selected Cell:", selectedCell);
    console.log("Active Toolbar:", activeToolbar);

    if (selectedTable) {
      const tbody = selectedTable.querySelector("tbody") || selectedTable;
      console.log("Table rows:", tbody.rows.length);
      console.log("Table cols:", tbody.rows[0]?.cells.length || 0);
    }

    console.log("=======================");
  };

  const highlightSelectedCell = () => {
    const allCells = editorRef.current?.querySelectorAll("td");
    allCells?.forEach((cell) => {
      cell.style.backgroundColor = "";
      cell.style.outline = "";
    });

    if (selectedCell) {
      selectedCell.style.backgroundColor = "#e3f2fd";
      selectedCell.style.outline = "2px solid #2196f3";
    }
  };

  useEffect(() => {
    highlightSelectedCell();
  }, [selectedCell]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const tempDiv = document.createElement("div");
      if (editorRef.current) {
        const children = Array.from(editorRef.current.childNodes);
        children.forEach((child) => tempDiv.appendChild(child.cloneNode(true)));
        tempDiv.removeAttribute("class");
        tempDiv.removeAttribute("style");
        tempDiv.removeAttribute("id");
      }

      const finalContent = tempDiv.innerHTML || "";

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title || "Untitled Blog");
      formDataToSend.append("alt_text", formData.altTitle || "");
      formDataToSend.append("meta_title", formData.metaTitle || "");
      formDataToSend.append("meta_description", formData.metaDescription || "");
      formDataToSend.append("focus_key", formData.focusKey || "");
      formDataToSend.append("description", finalContent);

      // Determine action based on mode
      formDataToSend.append("action", isEditMode ? "update" : "create");

      // Add ID if in edit mode
      if (isEditMode && formData.id) {
        formDataToSend.append("id", formData.id);
      }

      // Append the image file if it exists
      if (formData.blogImage) {
        formDataToSend.append("image", formData.blogImage);
      } else if (isEditMode && blogData?.image) {
        // If in edit mode and no new image was selected, send the existing image path
        const existingImagePath = blogData.image.split("/").pop();
        formDataToSend.append("existing_image", existingImagePath);

        // formDataToSend.append("existing_image", blogData.image);
      }

      const response = await fetch(
        "https://websiteadmin.insugo.in/api/blog.php",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Success:", result);

      toast({
        title: isEditMode
          ? "Blog updated successfully!"
          : "Blog post submitted successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Reset the form only if not in edit mode
      if (!isEditMode) {
        setFormData({
          title: "",
          altTitle: "",
          metaTitle: "",
          metaDescription: "",
          focusKey: "",
          blogImage: null,
          id: null,
        });
        setEditorContent("");
        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
      }
    } catch (error) {
      console.error("Error submitting blog post:", error);
      toast({
        title: isEditMode
          ? "Failed to update blog post"
          : "Failed to submit blog post",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderToolbar = () => {
    const commonTools = (
      <HStack spacing={1}>
        <ToolbarButton
          icon={ImBold}
          onClick={() => execCommand("bold")}
          label="Bold"
        />
        <ToolbarButton
          icon={RxSlash}
          onClick={() => execCommand("italic")}
          label="Italic"
        />
      </HStack>
    );

    switch (activeToolbar) {
      case "heading":
        return (
          <HStack spacing={1} align="center">
            <Box
              fontSize="2xl"
              fontWeight="bold"
              color="gray.800"
              mr={2}
              border="1px"
              borderColor="gray.300"
              p={2}
              borderRadius="5px"
            >
              <FaHeading />
            </Box>
            <HStack
              spacing={0}
              border="1px"
              borderColor="gray.200"
              borderRadius="6px"
              align="center"
            >
              <Box position="relative">
                <Menu
                  closeOnSelect={true}
                  placement="bottom-start"
                  strategy="fixed"
                >
                  {({ isOpen }) => (
                    <>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDown size={16} />}
                        variant="ghost"
                        size="sm"
                        px={3}
                        py={2}
                        minW="60px"
                        onMouseDown={(e) => e.preventDefault()} // Prevent focus issues
                      >
                        H1
                      </MenuButton>
                      <MenuList
                        maxH="200px"
                        overflowY="auto"
                        zIndex={1000}
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="lg"
                        onWheel={(e) => {
                          // Allow scrolling within dropdown without propagating to parent
                          e.stopPropagation();
                        }}
                        onScroll={(e) => {
                          // Prevent scroll events from bubbling up
                          e.stopPropagation();
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6].map((level) => (
                          <MenuItem
                            key={level}
                            onMouseDown={(e) => e.preventDefault()} // Prevent selection loss
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              // Store selection before any DOM manipulation
                              const selection = window.getSelection();
                              const selectedText = selection.toString();

                              // Use setTimeout to ensure menu closes first
                              setTimeout(() => {
                                formatBlock(`h${level}`, selectedText);
                              }, 0);
                            }}
                            _hover={{
                              bg: "gray.100",
                            }}
                          >
                            <Text fontWeight="bold">H{level}</Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </>
                  )}
                </Menu>
              </Box>

              <Divider orientation="vertical" h={8} borderColor="gray.300" />

              <ToolbarButton
                icon={AlignLeft}
                onClick={() => execCommand("justifyLeft")}
                label="Align Left"
              />
              <ToolbarButton
                icon={AlignCenter}
                onClick={() => execCommand("justifyCenter")}
                label="Align Center"
              />
              <ToolbarButton
                icon={AlignRight}
                onClick={() => execCommand("justifyRight")}
                label="Align Right"
              />

              <Divider orientation="vertical" h={8} borderColor="gray.300" />

              {commonTools}
            </HStack>
          </HStack>
        );

      case "gallery":
        return <HStack spacing={1}></HStack>;

      // case "image":
      //   return (
      //     <Box border="1px" borderColor="gray.300" px={2} sm={{ px: 3 }} roundedTopLeft="8px" roundedBottomLeft="8px" w="80">
      //       <HStack spacing={{ base: 0.5, sm: 1 }}>
      //         <ToolbarButton icon={Image} label="Image" />
      //         <ToolbarButton icon={GripVertical} label="More options" />
      //         <ToolbarButton icon={ChevronDown} label="Dropdown" />
      //         <Divider orientation="vertical" h={14} borderColor="black" />
      //         <ToolbarButton icon={List} label="Dropdown" />
      //         <Divider orientation="vertical" h={14} borderColor="black" />
      //         <Button
      //           onClick={() => fileInputRef.current?.click()}
      //           color="black"
      //           px={4}
      //           py={2}
      //           rounded="md"
      //           _hover={{ bg: "gray.100" }}
      //         >
      //           Add image
      //         </Button>
      //       </HStack>
      //     </Box>
      //   );

      case "link":
        return (
          <HStack spacing={1} align="center">
            <Text fontSize="sm" fontWeight="medium" color="gray.700" mr={2}>
              Link
            </Text>
            <ToolbarButton icon={Edit3} onClick={addLink} label="Edit Link" />
            <ToolbarButton
              icon={ExternalLink}
              onClick={() => {
                const selection = window.getSelection();
                if (selection.anchorNode) {
                  let element = selection.anchorNode.parentElement;
                  while (element && element.tagName !== "A") {
                    element = element.parentElement;
                  }
                  if (element && element.tagName === "A") {
                    window.open(element.href, "_blank");
                  }
                }
              }}
              label="Open Link"
            />
            <Divider
              orientation="vertical"
              h={4}
              borderColor="gray.300"
              mx={2}
            />
            {commonTools}
            <Divider
              orientation="vertical"
              h={4}
              borderColor="gray.300"
              mx={2}
            />
            <ToolbarButton
              icon={Trash2}
              onClick={() => execCommand("unlink")}
              label="Remove Link"
              variant="danger"
            />
          </HStack>
        );

      case "quote":
        return (
          <HStack spacing={1} align="center">
            <Text fontSize="sm" fontWeight="medium" color="gray.700" mr={2}>
              Quote
            </Text>
            <ToolbarButton
              icon={Quote}
              onClick={insertQuote}
              label="Insert Quote"
            />
          </HStack>
        );

      case "list":
        return (
          <HStack spacing={1} align="center">
            <Box
              fontSize="2xl"
              fontWeight="bold"
              color="gray.800"
              mr={2}
              border="1px"
              borderColor="gray.300"
              p={1}
              borderRadius="5px"
            >
              <ToolbarButton
                icon={List}
                onClick={() => insertList("bullet")}
                label="Bullet List"
              />
            </Box>
            <Box
              display="flex"
              border="1px"
              borderColor="gray.200"
              borderRadius="6px"
              alignItems="center"
              px={2}
              gap={2}
            >
              <ToolbarButton
                icon={Logs}
                onClick={() => insertList("numbered")}
                label="Numbered List"
              />
              <ToolbarButton
                icon={GripVertical}
                onClick={() => insertList("alphabetical")}
                label="Alphabetical List (a, b, c, d...)"
              />

              <Box position="relative">
                <Box mb={3}>
                  <ToolbarButton
                    icon={MdKeyboardArrowUp}
                    onClick={() =>
                      setLineHeight((prev) => Math.max(prev - 0.1, 1))
                    }
                    label="Increase Line Height"
                  />
                </Box>
                <Box position="absolute" top={4}>
                  <ToolbarButton
                    icon={MdKeyboardArrowDown}
                    onClick={() =>
                      setLineHeight((prev) => Math.min(prev + 0.1, 3))
                    }
                    label="Decrease Line Height"
                  />
                </Box>
              </Box>

              <Divider orientation="vertical" h={11} borderColor="gray.400" />
              <ToolbarButton
                icon={AlignLeft}
                onClick={() => execCommand("justifyLeft")}
                label="Align Left"
              />
              <ToolbarButton
                icon={AlignRight}
                onClick={() => execCommand("justifyRight")}
                label="Align Right"
              />
            </Box>
          </HStack>
        );

      case "table":
        return (
          <HStack
            spacing={2}
            p={2}
            bg="white"
            rounded="md"
            border="1px"
            borderColor="gray.200"
          >
            <Text
              onClick={() => setShowTableGrid(true)}
              fontSize="sm"
              fontWeight="medium"
              color="gray.700"
            >
              <FaTable />
            </Text>
            <HStack spacing={1}>
              <ToolbarButton
                icon={AlignLeft}
                onClick={() => {
                  if (selectedCell) {
                    selectedCell.style.textAlign = "left";
                    handleEditorChange();
                  }
                }}
                label="Align Left"
              />
              <ToolbarButton
                icon={AlignCenter}
                onClick={() => {
                  if (selectedCell) {
                    selectedCell.style.textAlign = "center";
                    handleEditorChange();
                  }
                }}
                label="Align Center"
              />
              <ToolbarButton
                icon={AlignRight}
                onClick={() => {
                  if (selectedCell) {
                    selectedCell.style.textAlign = "right";
                    handleEditorChange();
                  }
                }}
                label="Align Right"
              />
              <Divider orientation="vertical" h={8} borderColor="gray.300" />

              {commonTools}
            </HStack>
          </HStack>
        );

      default:
        return <HStack spacing={1}>{commonTools}</HStack>;
    }
  };

  const menuItems = [
    {
      icon: <FaParagraph size={24} color="#333333" />,
      label: "Paragraph",
      onClick: () => {
        insertParagraph();
        setActiveToolbar("default");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <FaRegImages size={24} color="#333333" />,
      label: "Gallery",
      onClick: () => {
        setActiveToolbar("gallery");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <FaHeading size={24} color="#333333" />,
      label: "Heading",
      onClick: () => {
        setActiveToolbar("heading");
        onCloseMenu();
        setShowHeadingDropdown(true);
        editorRef.current?.focus();
      },
    },
    {
      icon: <GoListUnordered size={24} color="#333333" />,
      label: "List",
      onClick: () => {
        insertList(false);
        setActiveToolbar("list");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <BsFillChatLeftQuoteFill size={24} color="#333333" />,
      label: "Quote",
      onClick: () => {
        insertQuote();
        setActiveToolbar("quote");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <IoImagesOutline size={24} color="#333333" />,
      label: "Image",
      onClick: () => {
        fileInputRef.current?.click();
        setActiveToolbar("image");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <Link size={24} color="#333333" />,
      label: "Link",
      onClick: () => {
        addLink();
        setActiveToolbar("link");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
    {
      icon: <FaTable size={24} color="#333333" />,
      label: "Table",
      onClick: () => {
        setActiveToolbar("table");
        onCloseMenu();
        editorRef.current?.focus();
      },
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      minH="100vh"
      w="full"
      textAlign="start"
      bg="gray.100"
      px={{ base: 2, sm: 0, md: 6 }}
    >
      <Box
        flex={1}
        w="full"
        maxW="screen.xl"
        display="flex"
        flexDir="column"
        justifyContent="center"
      >
        <Box as="main" flex={1}>
          <Box w="full" py={4}>
            <Heading
              as="h1"
              fontSize="2xl"
              fontWeight="semibold"
              color="gray.800"
              px={{ base: 4, sm: 6 }}
            >
              {isEditMode ? "Edit Blog" : "Add Blog"}
            </Heading>

            <Box p={{ base: 4, sm: 5 }}>
              <Grid
                templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                gap={6}
                bg="white"
                px={{ base: 4, sm: 6, md: 10 }}
                py={5}
              >
                {/* Left Side */}
                <Box display="grid" gap={6}>
                  {["Title", "Alt Title", "Meta Title"].map((label, index) => {
                    const field = label.toLowerCase().replace(" ", "");
                    return (
                      <Box key={index}>
                        <Text
                          as="label"
                          display="block"
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                          mb={2}
                        >
                          {label}
                        </Text>
                        <Input
                          type="text"
                          value={formData[field]}
                          onChange={(e) =>
                            handleFormChange(field, e.target.value)
                          }
                          w="full"
                          px={3}
                          py={2}
                          border="1px"
                          borderColor="gray.300"
                          rounded="md"
                          _focus={{
                            outline: "none",
                            ring: "2px",
                            ringColor: "blue.500",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>

                {/* Right Side */}
                <Box display="grid" gap={6}>
                  {/* In your Blog Image section */}
                  <Box>
                    <Text
                      as="label"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.700"
                      mb={2}
                    >
                      Blog Image
                    </Text>
                    {isEditMode && blogData?.image && !formData.blogImage && (
                      <Box mb={4}>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          Current Image:
                        </Text>
                        <Image
                          src={
                            blogData.image.startsWith("http")
                              ? blogData.image
                              : `https://websiteadmin.insugo.in/${blogData.image}`
                          }
                          // src={`https://websiteadmin.insugo.in/${blogData.image}`}
                          // src={blogData.image}
                          alt={blogData.alt_text}
                          maxH="200px"
                          mb={2}
                        />
                      </Box>
                    )}
                    <Box
                      rounded="lg"
                      border="1px"
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.500" }}
                      transition="colors"
                    >
                      <Box
                        as="label"
                        display="flex"
                        flexDir={{ base: "column", sm: "row" }}
                        alignItems={{ base: "start", sm: "center" }}
                        gap={2}
                        cursor="pointer"
                      >
                        <Box
                          bg="#E9E9E9"
                          w={{ base: "full", sm: "full", lg: "40%" }}
                          textAlign="center"
                          px={6}
                          py={2}
                          color="black"
                          _hover={{ bg: "gray.300" }}
                          transition="colors"
                          rounded={{ base: "none", sm: "left" }}
                        >
                          Browse
                        </Box>
                        <Box
                          px={4}
                          py={2}
                          color="gray.600"
                          isTruncated
                          w="full"
                        >
                          {formData.blogImage
                            ? formData.blogImage.name
                            : isEditMode
                            ? "Keep existing image or choose new..."
                            : "Choose File..."}
                        </Box>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleBlogImageUpload}
                          display="none"
                          id="blog-image-upload"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Text
                      as="label"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.700"
                      mb={2}
                    >
                      Meta Description
                    </Text>
                    <Textarea
                      rows={1}
                      value={formData.metaDescription}
                      onChange={(e) =>
                        handleFormChange("metaDescription", e.target.value)
                      }
                      w="full"
                      px={3}
                      py={2}
                      border="1px"
                      borderColor="gray.300"
                      rounded="md"
                      resize="none"
                      _focus={{
                        outline: "none",
                        ring: "2px",
                        ringColor: "blue.500",
                      }}
                    />
                  </Box>

                  <Box mt="-7px">
                    <Text
                      as="label"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.700"
                      mb={2}
                    >
                      Focus Key
                    </Text>
                    <Input
                      type="text"
                      value={formData.focusKey}
                      onChange={(e) =>
                        handleFormChange("focusKey", e.target.value)
                      }
                      w="full"
                      px={3}
                      py={2}
                      border="1px"
                      borderColor="gray.300"
                      rounded="md"
                      _focus={{
                        outline: "none",
                        ring: "2px",
                        ringColor: "blue.500",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Box
                position="relative"
                bg="white"
                px={{ base: 4, sm: 6, md: 10 }}
                py={5}
                mt={0}
                ref={editorWrapperRef}
              >
                <Flex
                  flexDir={{ base: "column", sm: "row" }}
                  alignItems={{ base: "start", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  mb={4}
                >
                  <Text
                    as="label"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                  >
                    Description
                  </Text>
                  <IconButton
                    onClick={onToggleMenu}
                    w={8}
                    h={8}
                    bg="gray.800"
                    _hover={{ bg: "gray.700" }}
                    color="white"
                    rounded="md"
                    aria-label="Add content"
                    icon={<Plus boxSize={4} color="#FFCC29" />}
                  />
                </Flex>

                <Box
                  bg="white"
                  border="1px"
                  borderColor="gray.200"
                  roundedTop="lg"
                  px={4}
                  py={3}
                  overflowX="auto"
                >
                  {renderToolbar()}

                  <Box mt={3}>
                    {activeToolbar === "gallery" && (
                      <Box
                        display="flex"
                        flexDir={{ base: "column", sm: "row" }}
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Heading
                          as="h3"
                          fontSize="base"
                          fontWeight="medium"
                          color="gray.800"
                        >
                          Gallery
                        </Heading>
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          bg="#FFDD55"
                          _hover={{ bg: "yellow.200" }}
                          color="black"
                          px={4}
                          py={2}
                          rounded="md"
                          transition="colors"
                        >
                          Upload
                        </Button>
                      </Box>
                    )}

                    {activeToolbar === "image" && (
                      <Box display="grid" gap={2}>
                        <Heading
                          as="h3"
                          fontSize="base"
                          fontWeight="medium"
                          color="gray.800"
                        >
                          Image
                        </Heading>
                        <Text fontSize="sm" color="gray.600">
                          Drag and drop an image, upload or choose from your
                          library.
                        </Text>

                        <Flex flexWrap="wrap" gap={2} mt={2}>
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            bg="#FFDD55"
                            _hover={{ bg: "yellow.300" }}
                            color="black"
                            px={4}
                            py={2}
                            rounded="md"
                            transition="colors"
                          >
                            Upload
                          </Button>

                          <Box
                            as="label"
                            cursor="pointer"
                            border="1px"
                            borderColor="#FFDD55"
                            color="gray.800"
                            px={4}
                            py={2}
                            rounded="md"
                            transition="colors"
                            display="inline-block"
                            htmlFor="mediaLibrary"
                          >
                            Media Library
                          </Box>

                          <Button
                            onClick={addImageFromURL}
                            border="1px"
                            borderColor="#FFDD55"
                            color="gray.800"
                            px={4}
                            py={2}
                            rounded="md"
                            transition="colors"
                          >
                            Insert from URL
                          </Button>
                        </Flex>

                        <Input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          display="none"
                          onChange={addImagesFromFile}
                        />

                        <Input
                          id="mediaLibrary"
                          type="file"
                          accept="image/*"
                          multiple
                          display="none"
                          onChange={addImagesFromFile}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box
                  minH={96}
                  position="relative"
                  border="1px"
                  borderColor="gray.200"
                  roundedBottom="lg"
                  mt={1}
                >
                  <Box
                    ref={editorRef}
                    contentEditable
                    onInput={handleEditorChange}
                    w="full"
                    h="full"
                    minH={80}
                    p={{ base: 4, sm: 6 }}
                    color="gray.800"
                    _focus={{ outline: "none" }}
                    fontSize="16px"
                    lineHeight={lineHeight}
                    suppressContentEditableWarning={true}
                    _empty={{
                      _before: {
                        content: '"Start writing your blog..."',
                        color: "gray.400",
                      },
                    }}
                    css={{
                      a: {
                        color: "blue.500",
                        textDecoration: "underline",
                        cursor: "pointer",
                        "&:hover": {
                          color: "blue.700",
                        },
                      },
                    }}
                  >
                    <Box
                      id="media-library-preview"
                      display="flex"
                      flexWrap="wrap"
                      gap={4}
                      mt={4}
                    />
                  </Box>
                </Box>

                {/* Table floating toolbar */}
                {/* Table floating toolbar */}
                {tableToolbarPos.visible && (
                  <Box
                    position="absolute"
                    zIndex={50}
                    bg="white"
                    rounded="md"
                    shadow="lg"
                    border="1px"
                    borderColor="gray.200"
                    p={1}
                    display="flex"
                    alignItems="center"
                    style={{
                      top: `${tableToolbarPos.top}px`,
                      left: `${tableToolbarPos.left}px`,
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <ToolbarButton
                      icon={Table}
                      onClick={() => {}}
                      label="Table"
                    />

                    <Box position="relative">
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<ChevronDown />}
                          aria-label="Row options"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setOpenTableMenu(
                              openTableMenu === "row" ? null : "row"
                            )
                          }
                        />
                        <MenuList>
                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertRow("above");
                              setOpenTableMenu(null);
                            }}
                          >
                            Insert row above
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertRow("below");
                              setOpenTableMenu(null);
                            }}
                          >
                            Insert row below
                          </MenuItem>
                          <MenuItem
                            color="red.600"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeRow();
                              setOpenTableMenu(null);
                            }}
                          >
                            Delete row
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Box>

                    <Box position="relative">
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<ChevronDown />}
                          aria-label="Column options"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setOpenTableMenu(
                              openTableMenu === "col" ? null : "col"
                            )
                          }
                        />
                        <MenuList>
                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertColumn("left");
                              setOpenTableMenu(null);
                            }}
                          >
                            Insert column left
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertColumn("right");
                              setOpenTableMenu(null);
                            }}
                          >
                            Insert column right
                          </MenuItem>
                          <MenuItem
                            color="red.600"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeColumn();
                              setOpenTableMenu(null);
                            }}
                          >
                            Delete column
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Box>

                    <ToolbarButton
                      icon={Trash2}
                      onClick={removeTable}
                      label="Delete table"
                      variant="danger"
                    />
                  </Box>
                )}

                {/* Table edge handles */}
                {handlesRect && (
                  <>
                    {/* Top handle */}
                    <Box
                      position="absolute"
                      zIndex={40}
                      w={6}
                      h={6}
                      bg="blue.500"
                      rounded="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      cursor="pointer"
                      _hover={{ bg: "blue.600" }}
                      style={{
                        top: `${handlesRect.top - 12}px`,
                        left: `${
                          handlesRect.left + handlesRect.width / 2 - 12
                        }px`,
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        insertRow("above");
                      }}
                    >
                      <Plus size={16} />
                    </Box>

                    {/* Bottom handle */}
                    <Box
                      position="absolute"
                      zIndex={40}
                      w={6}
                      h={6}
                      bg="blue.500"
                      rounded="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      cursor="pointer"
                      _hover={{ bg: "blue.600" }}
                      style={{
                        top: `${handlesRect.top + handlesRect.height + 6}px`,
                        left: `${
                          handlesRect.left + handlesRect.width / 2 - 12
                        }px`,
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        insertRow("below");
                      }}
                    >
                      <Plus size={16} />
                    </Box>

                    {/* Left handle */}
                    <Box
                      position="absolute"
                      zIndex={40}
                      w={6}
                      h={6}
                      bg="blue.500"
                      rounded="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      cursor="pointer"
                      _hover={{ bg: "blue.600" }}
                      style={{
                        top: `${
                          handlesRect.top + handlesRect.height / 2 - 12
                        }px`,
                        left: `${handlesRect.left - 12}px`,
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        insertColumn("left");
                      }}
                    >
                      <Plus size={16} />
                    </Box>

                    {/* Right handle */}
                    <Box
                      position="absolute"
                      zIndex={40}
                      w={6}
                      h={6}
                      bg="blue.500"
                      rounded="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      cursor="pointer"
                      _hover={{ bg: "blue.600" }}
                      style={{
                        top: `${
                          handlesRect.top + handlesRect.height / 2 - 12
                        }px`,
                        left: `${handlesRect.left + handlesRect.width + 6}px`,
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        insertColumn("right");
                      }}
                    >
                      <Plus size={16} />
                    </Box>
                  </>
                )}

                {/* Floating Menu */}
                {isMenuOpen && (
                  <Box
                    position="absolute"
                    right={{ base: 3, sm: 10, lg: 10 }}
                    top={16}
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                    rounded="lg"
                    shadow="lg"
                    p={4}
                    w={{ base: "72", sm: "80" }}
                    zIndex={50}
                  >
                    <Box position="relative" mb={4}>
                      <Search
                        position="absolute"
                        left={3}
                        top="50%"
                        transform="translateY(-50%)"
                        color="yellow.400"
                        boxSize={4}
                      />
                      <Input
                        type="text"
                        placeholder="Search here..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        w="full"
                        pl={10}
                        pr={4}
                        py={2}
                        border="1px"
                        borderColor="gray.200"
                        rounded="lg"
                        _focus={{
                          outline: "none",
                          ring: "2px",
                          ringColor: "blue.500",
                        }}
                      />
                    </Box>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      {filteredMenuItems.map((item, index) => (
                        <Box
                          key={index}
                          display="flex"
                          flexDir="column"
                          alignItems="center"
                          justifyContent="center"
                          p={2}
                          rounded="lg"
                          _hover={{ bg: "gray.100" }}
                          cursor="pointer"
                          transition="colors"
                          transitionDuration="200ms"
                          onClick={item.onClick}
                        >
                          <Box
                            w={8}
                            h={8}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="gray.600"
                            mb={1}
                          >
                            {item.icon}
                          </Box>
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            textAlign="center"
                          >
                            {item.label}
                          </Text>
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Table grid selector */}
                {showTableGrid && (
                  <Box
                    position="absolute"
                    zIndex={50}
                    bg="white"
                    p={4}
                    border="1px"
                    borderColor="gray.200"
                    rounded="lg"
                    shadow="lg"
                    style={{
                      top: "100px",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    <Text mb={2} fontSize="sm" fontWeight="medium">
                      Select table size
                    </Text>
                    <Grid templateColumns="repeat(6, 1fr)" gap={1}>
                      {Array.from({ length: 6 }).map((_, rowIndex) =>
                        Array.from({ length: 6}).map((_, colIndex) => (
                          <Box
                            key={`${rowIndex}-${colIndex}`}
                            w={8}
                            h={8}
                            border="1px"
                            bg={
                              tableSize.rows > rowIndex &&
                              tableSize.cols > colIndex
                                ? "blue.500"
                                : "gray.100"
                            }
                            _hover={{ bg: "blue.300" }}
                            cursor="pointer"
                            onMouseEnter={() =>
                              setTableSize({
                                rows: rowIndex + 1,
                                cols: colIndex + 1,
                              })
                            }
                            onClick={() =>
                              insertTable(rowIndex + 1, colIndex + 1)
                            }
                          />
                        ))
                      )}
                    </Grid>
                    <Text mt={2} textAlign="center" fontSize="sm">
                      {tableSize.rows} × {tableSize.cols}
                    </Text>
                  </Box>
                )}

                <Button
                  onClick={handleSubmit}
                  mt={4}
                  w={{ base: "full", sm: "auto" }}
                  bg="blue.600"
                  color="white"
                  px={6}
                  py={2}
                  rounded="md"
                  _hover={{ bg: "blue.700" }}
                  transition="colors"
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? "Updating..." : "Submitting..."}
                >
                  {isEditMode ? "Update Blog Post" : "Submit Blog Post"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Hidden File Input */}
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={addImagesFromFile}
        display="none"
      />
    </Box>
  );
}
